import { Unit } from '@/types';
import { isNil, defaults } from 'lodash/fp';

const renderUnit: (unit: Unit) => string = (unit) => {
  switch (unit.type) {
    case 'g':
      return 'g';
    case 'kg':
      return 'kg';
    case 't':
      return 't';
    case 'kt':
      return 'kt';
    case 'megatonnes':
      return 'Mt';
    case 'gigatonnes':
      return 'Gt';
    case 'megajoules':
      return 'MJ';
    case 'gigajoules':
      return 'GJ';
    case 'terajoules':
      return 'TJ';
    case 'sqm':
      return 'm\u00B2';
    // eslint-disable-next-line max-len
    /* Ref for unit format - http://www.cleanenergyregulator.gov.au/NGER/About-the-National-Greenhouse-and-Energy-Reporting-scheme/Greenhouse-gases-and-energy */
    case 'kwh':
      return 'kWh';
    case 'kw':
      return 'kW';
    case 'megawatt-hours':
      return 'MWh';
    case 'gigawatt-hours':
      return 'GWh';
    case 'pounds-co2e':
      return 'lb\u00A0CO\u2082‑e';
    case 'g-co2e':
      return 'g\u00A0CO\u2082‑e';
    case 'kg-co2e':
      return 'kg\u00A0CO\u2082‑e';
    case 't-co2e':
      return 't\u00A0CO\u2082‑e';
    case 'kt-co2e':
      return 'kt\u00A0CO\u2082‑e';
    case 'megatonnes-co2e':
      return 'Mt\u00A0CO\u2082‑e';
    case 'gigatonnes-co2e':
      return 'Gt\u00A0CO\u2082‑e';
    case 'litres':
      return 'L';
    case 'kilolitres':
      return 'kL';
    case 'km':
      return 'km';
    case 'ntk':
      return 'ntk';
    case 'tkm':
      return 'tkm';
    case 'head':
      return 'head';
    case 'hectares':
      return 'ha';
    case 'dwellings':
      return 'dwellings';
    case 'employees':
      return 'employees';
    case 'jobs':
      return 'jobs';
    case 'installations':
      return 'installations';
    case 'pkm':
      return 'pkm';
    case 'people':
      return 'people';
    case 'currency':
      return unit.currency;
    case 'rate':
      const suffix = unit.of ? ` of ${unit.of}` : '';
      return `${renderUnit(unit.numerator)} per ${renderUnit(unit.per)}${suffix}`;
    case 'count':
      return unit.label ? unit.label : '';
    case 'percentage':
      return '%';
    case 'cbm':
      return 'm³';
  }
};

const renderValue = (value: number, unit: Unit, locale?: Intl.Locale) =>
  value.toLocaleString(
    locale,
    defaults(
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      },
      {
        minimumSignificantDigits: unit.minimumSignificantDigits,
        maximumSignificantDigits: unit.maximumSignificantDigits,
        minimumFractionDigits: unit.minimumFractionDigits,
        maximumFractionDigits: unit.maximumFractionDigits,
      },
    ),
  );

const renderValueOptional = (
  value: number | undefined,
  unit: Unit,
  locale?: Intl.Locale,
) => (isNil(value) ? '' : renderValue(value, unit, locale));

const render = (value: number, unit: Unit, locale?: Intl.Locale) => {
  if (unit.type === 'currency') {
    return value.toLocaleString(locale, {
      style: 'currency',
      currency: unit.currency,
    });
  }

  return `${renderValue(value, unit, locale)} ${renderUnit(unit)}`;
};

// Format a server-computed `share` (a 0–1 fraction) as a whole-number
// percent, e.g. 0.6656 → "67%". Used by the disclosure tables' % column.
// `Intl`'s percent style does the ×100 + glyph — display formatting, not a
// data transform (the FE never derives the share; the server sends it).
const renderShare = (share: number, locale?: Intl.Locale) =>
  share.toLocaleString(locale, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

export { render, renderShare, renderUnit, renderValue, renderValueOptional };
